import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import { useSelectionFragmentProp } from '@platejs/utils/react';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import styles from './BlockTypeMenu.module.scss';

type BlockType = 'p' | 'h1' | 'h2' | 'h3' | 'blockquote';

const HEADING_TYPES = ['h1', 'h2', 'h3'];

// A heading's size + rhythm come from the h1/h2/h3 CSS rules. But the
// FontSize/LineHeight inject plugins paint a leaf-level `fontSize` mark and a
// block-level `lineHeight` as INLINE styles, which beat that CSS. Converting
// sized body text to a heading (toggleBlock keeps marks/props) therefore
// renders every heading at the old body size — the preset looks broken.
// Picking a heading is a deliberate "apply this block style" action, so clear
// the conflicting character/block size overrides across the selection and let
// the heading CSS win. Layout props (align, indent, list) are untouched.
function clearHeadingSizeOverrides(ed: any) {
    try {
        ed?.tf?.removeMarks?.('fontSize');
        ed?.tf?.unsetNodes?.(['lineHeight', 'fontSize'], {
            match: (n: any) => HEADING_TYPES.includes(n?.type),
            mode: 'lowest',
        });
    } catch {
        /* defensive */
    }
}

interface Option {
    value: BlockType;
    label: string;
    preview: React.ReactNode;
    hint?: string;
}

const OPTIONS: Option[] = [
    {
        value: 'p',
        label: 'Text',
        preview: <span className={styles.previewText}>Text</span>,
        hint: 'Plain paragraph',
    },
    {
        value: 'h1',
        label: 'Heading 1',
        preview: <span className={styles.previewH1}>Heading 1</span>,
        hint: 'Big section heading',
    },
    {
        value: 'h2',
        label: 'Heading 2',
        preview: <span className={styles.previewH2}>Heading 2</span>,
        hint: 'Medium section heading',
    },
    {
        value: 'h3',
        label: 'Heading 3',
        preview: <span className={styles.previewH3}>Heading 3</span>,
        hint: 'Small section heading',
    },
    {
        value: 'blockquote',
        label: 'Quote',
        preview: (
            <span className={styles.previewQuote}>
                <FormatQuoteIcon className={styles.quoteIcon} fontSize="small" /> Quote
            </span>
        ),
        hint: 'Pull quote / blockquote',
    },
];

const KNOWN: BlockType[] = ['p', 'h1', 'h2', 'h3', 'blockquote'];

function activeOption(type: string | undefined): Option {
    const safe = KNOWN.includes((type as BlockType) ?? 'p') ? (type as BlockType) : 'p';
    return OPTIONS.find((o) => o.value === safe) ?? OPTIONS[0];
}

export function BlockTypeMenu() {
    const editor = useEditorRef();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Library hook: reads node `type` across the current selection fragment.
    // Returns the value if uniform, undefined if mixed — same shape we used
    // to compute by hand via useEditorSelector + editor.api.block.
    const blockType: string | undefined = (useSelectionFragmentProp as any)({
        defaultValue: 'p',
        getProp: (node: any) => node?.type,
    });
    const current = activeOption(blockType);

    useEffect(() => {
        if (!open) return;
        function handleClickOutside(e: any) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        function handleEsc(e: any) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open]);

    const apply = (value: BlockType) => (e: React.MouseEvent) => {
        e.preventDefault();
        const ed: any = editor;
        try {
            if (value === 'p') {
                // Toggle whatever heading/quote is active OFF — Plate's tf.[type].toggle
                // on the current block type collapses it back to paragraph.
                const currentType = ed?.api?.block?.({ block: true })?.[0]?.type;
                if (currentType && currentType !== 'p') {
                    ed?.tf?.[currentType]?.toggle?.();
                }
            } else {
                ed?.tf?.[value]?.toggle?.();
                if (HEADING_TYPES.includes(value)) clearHeadingSizeOverrides(ed);
            }
        } catch {
            /* defensive */
        }
        setOpen(false);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(styles.trigger, open && styles.triggerOpen)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Block type"
                title="Block type"
            >
                <span className={styles.triggerLabel}>{current.label}</span>
                <ArrowDropDownRoundedIcon className={styles.caret} fontSize="small" />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick block type">
                    {OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            role="menuitem"
                            className={clsx(styles.item, current.value === opt.value && styles.itemActive)}
                            onMouseDown={apply(opt.value)}
                        >
                            <span className={styles.itemPreview}>{opt.preview}</span>
                            {opt.hint && <span className={styles.itemHint}>{opt.hint}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
